import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Tabs, Col } from 'antd';
import { PaymentMode, TransactionType } from '@portal/models/enumerators';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { getRouteStackPath } from '@portal/config/routes';
import { ReceivableActions, SettingsActions } from '@portal/actions';
import { translate } from '@portal/services/i18n';

import PanelContentBreadcrumb from '@portal/components/PanelContentBreadcrumb/PanelContentBreadcrumb';
import ManualReceivableModal from '@portal/components/ManualReceivableModal/ManualReceivableModal';
import PanelContentHeader from '@portal/components/PanelContentHeader/PanelContentHeader';
import DataTable from '@portal/components/DataTable/DataTable';
import useReduxState from '@portal/hooks/useReduxState';
import MaintenanceModal from '@portal/components/MaintenanceModal/MaintenanceModal';
import ReceivableButtons from '@portal/components/ReceivableButtons/ReceivableButtons';

const initialValues: advancedFilterModels.baseFilter = {
  orderBy: 'createdAt',
  page: 0,
  pageSize: 10,
  sort: 'desc',
};

const AccountReceivable = () => {
  const dispatch = useDispatch();

  const [tabKey, setTabKey] = useState<string>('1');
  const [advancedFilters, setAdvancedFilters] = useState(initialValues);
  const [manualPaymentModal, setManualPaymentModal] = useState<{
    visible: boolean;
    type: TransactionType | null;
  }>({
    visible: false,
    type: null,
  });
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);

  const {
    receivable: { receivableGroupsReport, countReceivableGroup },
    settings: { settings, remoteConfig },
  } = useReduxState();

  const TabTitle = (title: string) => (
    <>
      <p className="account-receivable__tabs__title">{title}</p>
    </>
  );

  const getPaymentRows = (filters: advancedFilterModels.baseFilter) => {
    const filtersCopy = {
      ...advancedFilters,
      ...filters,
    };
    setAdvancedFilters(filtersCopy);

    dispatch(ReceivableActions.getReceivablesGroups(filtersCopy));
  };

  const downloadReturnFile = (fileId: string) => {
    dispatch(ReceivableActions.getExportedResponseCnab(fileId ?? ''));
  };

  useEffect(() => {
    dispatch(ReceivableActions.getReceivablesGroups(initialValues));
  }, [dispatch]);

  useEffect(() => {
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig]);

  const openMaintenance = () => {
    setOpenMaintenanceModal(true);

    return;
  };

  return (
    <>
      <Col className="account-receivable">
        <div className="account-receivable__container">
          <PanelContentBreadcrumb
            items={[
              {
                active: true,
                title: translate('COMPONENTS.BREADCRUMB.HOME'),
                url: getRouteStackPath('HOME', 'HOME_PAGE'),
              },
              {
                active: false,
                title: translate(`PAGES.PAYMENT.ACCOUNT_RECEIVABLE.TITLE`),
              },
            ]}
          />

          <div className="account-receivable__container__last-payment-header">
            <PanelContentHeader
              pageTitle={translate(`PAGES.PAYMENT.ACCOUNT_RECEIVABLE.TITLE`)}
              pageDescription={translate(
                `PAGES.PAYMENT.ACCOUNT_RECEIVABLE.DESCRIPTION`
              )}
            />
          </div>

          <ReceivableButtons
            openMaintenance={openMaintenance}
            setManualPaymentModal={setManualPaymentModal}
          />

          <div className="account-receivable__container__tabs">
            <Tabs
              defaultActiveKey="1"
              activeKey={tabKey}
              onChange={(key) => setTabKey(key)}
            >
              <Tabs.TabPane
                tab={TabTitle(
                  translate('PAGES.PAYMENT.ACCOUNT_RECEIVABLE.TABS.REPORT')
                )}
                key="1"
                className="account-receivable__container__tabs__content-container"
              >
                <div>
                  <DataTable
                    columns={[
                      {
                        field: 'id',
                        flex: 1,
                        headerName: 'id',
                        sortable: false,
                        hide: true,
                      },
                      {
                        headerName: translate(
                          'PAGES.PAYMENT.ACCOUNT_RECEIVABLE.COLUMNS.CREATED_AT'
                        ),
                        field: 'createdAt',
                        flex: 1,
                        renderCell: (column) =>
                          DateTime.fromISO(column.row.createdAt).toLocaleString(
                            DateTime.DATETIME_SHORT
                          ),
                      },
                      {
                        field: 'fileName',
                        flex: 1,
                        headerName: translate(
                          'PAGES.PAYMENT.ACCOUNT_RECEIVABLE.COLUMNS.FILE_NAME'
                        ),
                        renderCell: (column) =>
                          column.row?.referenceName && (
                            <>{column.row.referenceName}</>
                          ),
                      },
                      {
                        field: 'sent-file',
                        flex: 1,
                        headerName: translate(
                          'PAGES.PAYMENT.ACCOUNT_RECEIVABLE.COLUMNS.SENT_FILE'
                        ),
                        headerAlign: 'center',
                        align: 'center',
                        renderCell: (column) => (
                          <Tooltip
                            title={
                              !column.row.bucketUrl
                                ? translate(
                                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.TOOLTIP.CANNOT_DOWNLOAD'
                                  )
                                : ''
                            }
                          >
                            <button
                              className="transparent-button"
                              type="button"
                              onClick={() =>
                                window.location.assign(column.row.bucketUrl)
                              }
                              disabled={!column.row.bucketUrl}
                            >
                              <DownloadOutlined />
                            </button>
                          </Tooltip>
                        ),
                      },
                      {
                        field: 'return-file',
                        flex: 1,
                        headerName: translate(
                          'PAGES.PAYMENT.ACCOUNT_RECEIVABLE.COLUMNS.RETURN_FILE'
                        ),
                        headerAlign: 'center',
                        align: 'center',
                        renderCell: (column) => (
                          <Tooltip
                            title={
                              !column.row.bucket
                                ? translate(
                                    'PAGES.PAYMENT.ACCOUNT_PAYABLE.TOOLTIP.CANNOT_DOWNLOAD'
                                  )
                                : ''
                            }
                          >
                            <button
                              className="transparent-button"
                              type="button"
                              onClick={() => downloadReturnFile(column.row.id)}
                              disabled={!column.row.bucket}
                            >
                              <DownloadOutlined />
                            </button>
                          </Tooltip>
                        ),
                      },
                      {
                        field: 'action',
                        align: 'right',
                        headerName: '',
                        renderCell: (column) => (
                          <div className="account-receivable__details">
                            <Link
                              to={{
                                pathname: `/arquivos/detalhes-arquivos/${column.row.id}`,
                                search: `?mode=${PaymentMode.RECEIVABLE}`,
                              }}
                            >
                              <button
                                className="transparent-button users__details--button"
                                type="button"
                              >
                                <EyeOutlined />
                              </button>
                            </Link>
                          </div>
                        ),
                      },
                    ]}
                    rows={receivableGroupsReport ?? []}
                    page={advancedFilters.page}
                    pageSize={advancedFilters.pageSize}
                    onChange={(filters) => {
                      const searchFilters = {
                        ...advancedFilters,
                        ...filters,
                      };
                      setAdvancedFilters(searchFilters);
                      getPaymentRows(searchFilters);
                    }}
                    rowCount={countReceivableGroup}
                    orderBy={advancedFilters.orderBy}
                    sort={advancedFilters.sort}
                  />
                </div>
              </Tabs.TabPane>
            </Tabs>
          </div>
          <ManualReceivableModal
            maxDaysDuePaymentInvoice={settings?.maxDaysDueDatePaymentInvoice}
            setVisible={(visible) =>
              setManualPaymentModal((prev) => ({ ...prev, visible }))
            }
            visible={manualPaymentModal.visible}
            type={manualPaymentModal.type}
          />
        </div>
      </Col>
      <MaintenanceModal
        setVisible={setOpenMaintenanceModal}
        visible={openMaintenanceModal}
      />
    </>
  );
};

export default AccountReceivable;
